/**
 * CARTAL locations registry — Saudi Arabia (all 13 regions), GCC, and
 * neighbouring countries.  Used by the cascading LocationPicker.
 *
 * Shape:
 *   COUNTRIES = [{
 *     code, flag, ar, en,
 *     regions: [{ id, ar, en, cities: [{ id, ar, en }] }]
 *   }]
 *
 * `id` is the slug we send to the marketplace filter / showrooms API.
 * Saudi city slugs intentionally keep the English-pascal form already used
 * elsewhere in the code (Riyadh, Jeddah…) to stay backwards compatible.
 */

export const COUNTRIES = [
  // ── Saudi Arabia — primary market ──────────────────────────────────
  {
    code: "SA",
    flag: "🇸🇦",
    ar: "المملكة العربية السعودية",
    en: "Saudi Arabia",
    primary: true,
    regions: [
      {
        id: "riyadh-region", ar: "منطقة الرياض", en: "Riyadh Region",
        cities: [
          { id: "Riyadh", ar: "الرياض", en: "Riyadh" },
          { id: "Kharj", ar: "الخرج", en: "Al-Kharj" },
          { id: "Dawadmi", ar: "الدوادمي", en: "Dawadmi" },
          { id: "Majmaah", ar: "المجمعة", en: "Al-Majma'ah" },
          { id: "Quwayiyah", ar: "القويعية", en: "Quwayiyah" },
          { id: "Shaqra", ar: "شقراء", en: "Shaqra" },
          { id: "Zulfi", ar: "الزلفي", en: "Zulfi" },
          { id: "Wadi-Dawasir", ar: "وادي الدواسر", en: "Wadi Al-Dawasir" },
          { id: "Aflaj", ar: "الأفلاج", en: "Al-Aflaj" },
          { id: "Dilam", ar: "الدلم", en: "Al-Dilam" },
          { id: "Durma", ar: "ضرما", en: "Durma" },
          { id: "Huraymila", ar: "حريملاء", en: "Huraymila" },
        ],
      },
      {
        id: "makkah-region", ar: "منطقة مكة المكرمة", en: "Makkah Region",
        cities: [
          { id: "Makkah", ar: "مكة المكرمة", en: "Makkah" },
          { id: "Jeddah", ar: "جدة", en: "Jeddah" },
          { id: "Taif", ar: "الطائف", en: "Taif" },
          { id: "Qunfudhah", ar: "القنفذة", en: "Al-Qunfudhah" },
          { id: "Lith", ar: "الليث", en: "Al-Lith" },
          { id: "Rabigh", ar: "رابغ", en: "Rabigh" },
          { id: "Jumum", ar: "الجموم", en: "Al-Jumum" },
          { id: "Turbah", ar: "تربة", en: "Turbah" },
          { id: "Khurmah", ar: "الخرمة", en: "Al-Khurmah" },
          { id: "Khulays", ar: "خليص", en: "Khulays" },
          { id: "Kamil", ar: "الكامل", en: "Al-Kamil" },
        ],
      },
      {
        id: "madinah-region", ar: "منطقة المدينة المنورة", en: "Madinah Region",
        cities: [
          { id: "Madinah", ar: "المدينة المنورة", en: "Madinah" },
          { id: "Yanbu", ar: "ينبع", en: "Yanbu" },
          { id: "Ula", ar: "العلا", en: "Al-Ula" },
          { id: "Badr", ar: "بدر", en: "Badr" },
          { id: "Hanakiyah", ar: "الحناكية", en: "Al-Hanakiyah" },
          { id: "Khaybar", ar: "خيبر", en: "Khaybar" },
          { id: "Mahd-Dhahab", ar: "مهد الذهب", en: "Mahd Adh-Dhahab" },
        ],
      },
      {
        id: "qassim-region", ar: "منطقة القصيم", en: "Qassim Region",
        cities: [
          { id: "Buraidah", ar: "بريدة", en: "Buraidah" },
          { id: "Unaizah", ar: "عنيزة", en: "Unaizah" },
          { id: "Rass", ar: "الرس", en: "Ar-Rass" },
          { id: "Bukayriyah", ar: "البكيرية", en: "Al-Bukayriyah" },
          { id: "Badaie", ar: "البدائع", en: "Al-Badaie" },
          { id: "Mithnab", ar: "المذنب", en: "Al-Mithnab" },
          { id: "Asyah", ar: "الأسياح", en: "Al-Asyah" },
          { id: "Riyadh-Khabra", ar: "رياض الخبراء", en: "Riyadh Al-Khabra" },
          { id: "Uyun-Jawa", ar: "عيون الجواء", en: "Uyun Al-Jawa" },
        ],
      },
      {
        id: "eastern-region", ar: "المنطقة الشرقية", en: "Eastern Province",
        cities: [
          { id: "Dammam", ar: "الدمام", en: "Dammam" },
          { id: "Khobar", ar: "الخبر", en: "Al-Khobar" },
          { id: "Dhahran", ar: "الظهران", en: "Dhahran" },
          { id: "Hofuf", ar: "الأحساء (الهفوف)", en: "Al-Ahsa (Hofuf)" },
          { id: "Jubail", ar: "الجبيل", en: "Jubail" },
          { id: "Qatif", ar: "القطيف", en: "Qatif" },
          { id: "Hafr-Batin", ar: "حفر الباطن", en: "Hafr Al-Batin" },
          { id: "Nairyah", ar: "النعيرية", en: "An-Nu'ayriyah" },
          { id: "Abqaiq", ar: "بقيق", en: "Abqaiq" },
          { id: "Khafji", ar: "الخفجي", en: "Al-Khafji" },
          { id: "Ras-Tanura", ar: "رأس تنورة", en: "Ras Tanura" },
        ],
      },
      {
        id: "asir-region", ar: "منطقة عسير", en: "Asir Region",
        cities: [
          { id: "Abha", ar: "أبها", en: "Abha" },
          { id: "Khamis-Mushait", ar: "خميس مشيط", en: "Khamis Mushait" },
          { id: "Bisha", ar: "بيشة", en: "Bisha" },
          { id: "Mahayel-Asir", ar: "محايل عسير", en: "Muhayil Asir" },
          { id: "Namas", ar: "النماص", en: "An-Namas" },
          { id: "Dhahran-Janub", ar: "ظهران الجنوب", en: "Dhahran Al-Janub" },
          { id: "Sarat-Abidah", ar: "سراة عبيدة", en: "Sarat Abidah" },
          { id: "Tathlith", ar: "تثليث", en: "Tathlith" },
        ],
      },
      {
        id: "tabuk-region", ar: "منطقة تبوك", en: "Tabuk Region",
        cities: [
          { id: "Tabuk", ar: "تبوك", en: "Tabuk" },
          { id: "Wajh", ar: "الوجه", en: "Al-Wajh" },
          { id: "Duba", ar: "ضباء", en: "Duba" },
          { id: "Tayma", ar: "تيماء", en: "Tayma" },
          { id: "Umluj", ar: "أملج", en: "Umluj" },
          { id: "Haql", ar: "حقل", en: "Haql" },
          { id: "Bada", ar: "البدع", en: "Al-Bada" },
        ],
      },
      {
        id: "hail-region", ar: "منطقة حائل", en: "Hail Region",
        cities: [
          { id: "Hail", ar: "حائل", en: "Hail" },
          { id: "Baqaa", ar: "بقعاء", en: "Baqaa" },
          { id: "Ghazalah", ar: "الغزالة", en: "Al-Ghazalah" },
          { id: "Shinan", ar: "الشنان", en: "Ash-Shinan" },
          { id: "Hait", ar: "الحائط", en: "Al-Hait" },
        ],
      },
      {
        id: "northern-borders-region", ar: "منطقة الحدود الشمالية", en: "Northern Borders Region",
        cities: [
          { id: "Arar", ar: "عرعر", en: "Arar" },
          { id: "Rafha", ar: "رفحاء", en: "Rafha" },
          { id: "Turaif", ar: "طريف", en: "Turaif" },
          { id: "Uwayqilah", ar: "العويقيلة", en: "Al-Uwayqilah" },
        ],
      },
      {
        id: "jazan-region", ar: "منطقة جازان", en: "Jazan Region",
        cities: [
          { id: "Jazan", ar: "جازان", en: "Jazan" },
          { id: "Sabya", ar: "صبيا", en: "Sabya" },
          { id: "Abu-Arish", ar: "أبو عريش", en: "Abu Arish" },
          { id: "Samtah", ar: "صامطة", en: "Samtah" },
          { id: "Baysh", ar: "بيش", en: "Baysh" },
          { id: "Dair", ar: "الدائر", en: "Ad-Dair" },
          { id: "Ardah", ar: "العارضة", en: "Al-Ardah" },
          { id: "Darb", ar: "الدرب", en: "Ad-Darb" },
          { id: "Farasan", ar: "فرسان", en: "Farasan" },
        ],
      },
      {
        id: "najran-region", ar: "منطقة نجران", en: "Najran Region",
        cities: [
          { id: "Najran", ar: "نجران", en: "Najran" },
          { id: "Sharurah", ar: "شرورة", en: "Sharurah" },
          { id: "Hubuna", ar: "حبونا", en: "Hubuna" },
          { id: "Thar", ar: "ثار", en: "Thar" },
          { id: "Badr-Janub", ar: "بدر الجنوب", en: "Badr Al-Janub" },
        ],
      },
      {
        id: "bahah-region", ar: "منطقة الباحة", en: "Al-Baha Region",
        cities: [
          { id: "Bahah", ar: "الباحة", en: "Al-Baha" },
          { id: "Baljurashi", ar: "بلجرشي", en: "Baljurashi" },
          { id: "Mandaq", ar: "المندق", en: "Al-Mandaq" },
          { id: "Aqiq", ar: "العقيق", en: "Al-Aqiq" },
          { id: "Qura", ar: "القرى", en: "Al-Qura" },
          { id: "Makhwah", ar: "المخواة", en: "Al-Makhwah" },
        ],
      },
      {
        id: "jouf-region", ar: "منطقة الجوف", en: "Al-Jawf Region",
        cities: [
          { id: "Sakaka", ar: "سكاكا", en: "Sakaka" },
          { id: "Qurayyat", ar: "القريات", en: "Qurayyat" },
          { id: "Dumat-Jandal", ar: "دومة الجندل", en: "Dumat Al-Jandal" },
          { id: "Tabarjal", ar: "طبرجل", en: "Tabarjal" },
        ],
      },
    ],
  },
  // ── GCC ──────────────────────────────────────────────────────────────
  {
    code: "AE", flag: "🇦🇪", ar: "الإمارات العربية المتحدة", en: "United Arab Emirates",
    regions: [{
      id: "ae-emirates", ar: "الإمارات", en: "Emirates",
      cities: [
        { id: "Abu-Dhabi", ar: "أبوظبي", en: "Abu Dhabi" },
        { id: "Dubai", ar: "دبي", en: "Dubai" },
        { id: "Sharjah", ar: "الشارقة", en: "Sharjah" },
        { id: "Ajman", ar: "عجمان", en: "Ajman" },
        { id: "Umm-Al-Quwain", ar: "أم القيوين", en: "Umm Al Quwain" },
        { id: "Ras-Al-Khaimah", ar: "رأس الخيمة", en: "Ras Al Khaimah" },
        { id: "Fujairah", ar: "الفجيرة", en: "Fujairah" },
        { id: "Al-Ain", ar: "العين", en: "Al Ain" },
      ],
    }],
  },
  {
    code: "KW", flag: "🇰🇼", ar: "الكويت", en: "Kuwait",
    regions: [{
      id: "kw-cities", ar: "الكويت", en: "Kuwait",
      cities: [
        { id: "Kuwait-City", ar: "مدينة الكويت", en: "Kuwait City" },
        { id: "Salmiya", ar: "السالمية", en: "Salmiya" },
        { id: "Hawally", ar: "حولي", en: "Hawally" },
        { id: "Jahra", ar: "الجهراء", en: "Al-Jahra" },
        { id: "Ahmadi", ar: "الأحمدي", en: "Al-Ahmadi" },
        { id: "Farwaniya", ar: "الفروانية", en: "Farwaniya" },
        { id: "Mubarak-Kabeer", ar: "مبارك الكبير", en: "Mubarak Al-Kabeer" },
      ],
    }],
  },
  {
    code: "QA", flag: "🇶🇦", ar: "قطر", en: "Qatar",
    regions: [{
      id: "qa-cities", ar: "قطر", en: "Qatar",
      cities: [
        { id: "Doha", ar: "الدوحة", en: "Doha" },
        { id: "Rayyan", ar: "الريان", en: "Al Rayyan" },
        { id: "Wakrah", ar: "الوكرة", en: "Al Wakrah" },
        { id: "Mesaieed", ar: "مسيعيد", en: "Mesaieed" },
        { id: "Khor", ar: "الخور", en: "Al Khor" },
        { id: "Dukhan", ar: "دخان", en: "Dukhan" },
        { id: "Lusail", ar: "لوسيل", en: "Lusail" },
      ],
    }],
  },
  {
    code: "BH", flag: "🇧🇭", ar: "البحرين", en: "Bahrain",
    regions: [{
      id: "bh-cities", ar: "البحرين", en: "Bahrain",
      cities: [
        { id: "Manama", ar: "المنامة", en: "Manama" },
        { id: "Muharraq", ar: "المحرق", en: "Muharraq" },
        { id: "Riffa", ar: "الرفاع", en: "Riffa" },
        { id: "Isa-Town", ar: "مدينة عيسى", en: "Isa Town" },
        { id: "Hamad-Town", ar: "مدينة حمد", en: "Hamad Town" },
        { id: "Sitra", ar: "سترة", en: "Sitra" },
      ],
    }],
  },
  {
    code: "OM", flag: "🇴🇲", ar: "سلطنة عُمان", en: "Oman",
    regions: [{
      id: "om-cities", ar: "عُمان", en: "Oman",
      cities: [
        { id: "Muscat", ar: "مسقط", en: "Muscat" },
        { id: "Salalah", ar: "صلالة", en: "Salalah" },
        { id: "Nizwa", ar: "نزوى", en: "Nizwa" },
        { id: "Sohar", ar: "صحار", en: "Sohar" },
        { id: "Sur", ar: "صور", en: "Sur" },
        { id: "Buraimi", ar: "البريمي", en: "Al Buraimi" },
      ],
    }],
  },
  // ── Neighbouring countries ──────────────────────────────────────────
  {
    code: "JO", flag: "🇯🇴", ar: "الأردن", en: "Jordan",
    regions: [{
      id: "jo-cities", ar: "الأردن", en: "Jordan",
      cities: [
        { id: "Amman", ar: "عمّان", en: "Amman" },
        { id: "Zarqa", ar: "الزرقاء", en: "Zarqa" },
        { id: "Irbid", ar: "إربد", en: "Irbid" },
        { id: "Aqaba", ar: "العقبة", en: "Aqaba" },
        { id: "Salt", ar: "السلط", en: "As-Salt" },
        { id: "Madaba", ar: "مادبا", en: "Madaba" },
        { id: "Mafraq", ar: "المفرق", en: "Mafraq" },
      ],
    }],
  },
  {
    code: "EG", flag: "🇪🇬", ar: "مصر", en: "Egypt",
    regions: [{
      id: "eg-cities", ar: "مصر", en: "Egypt",
      cities: [
        { id: "Cairo", ar: "القاهرة", en: "Cairo" },
        { id: "Alexandria", ar: "الإسكندرية", en: "Alexandria" },
        { id: "Giza", ar: "الجيزة", en: "Giza" },
        { id: "Sharm", ar: "شرم الشيخ", en: "Sharm El-Sheikh" },
        { id: "Hurghada", ar: "الغردقة", en: "Hurghada" },
        { id: "Mansoura", ar: "المنصورة", en: "Mansoura" },
      ],
    }],
  },
  {
    code: "IQ", flag: "🇮🇶", ar: "العراق", en: "Iraq",
    regions: [{
      id: "iq-cities", ar: "العراق", en: "Iraq",
      cities: [
        { id: "Baghdad", ar: "بغداد", en: "Baghdad" },
        { id: "Basra", ar: "البصرة", en: "Basra" },
        { id: "Mosul", ar: "الموصل", en: "Mosul" },
        { id: "Erbil", ar: "أربيل", en: "Erbil" },
        { id: "Najaf", ar: "النجف", en: "Najaf" },
        { id: "Karbala", ar: "كربلاء", en: "Karbala" },
      ],
    }],
  },
  {
    code: "YE", flag: "🇾🇪", ar: "اليمن", en: "Yemen",
    regions: [{
      id: "ye-cities", ar: "اليمن", en: "Yemen",
      cities: [
        { id: "Sanaa", ar: "صنعاء", en: "Sanaa" },
        { id: "Aden", ar: "عدن", en: "Aden" },
        { id: "Taiz", ar: "تعز", en: "Taiz" },
        { id: "Hodeidah", ar: "الحديدة", en: "Hodeidah" },
        { id: "Mukalla", ar: "المكلا", en: "Mukalla" },
      ],
    }],
  },
];

/** Flat list — every city in every country.  Used by the search input. */
export const ALL_CITIES = COUNTRIES.flatMap((c) =>
  c.regions.flatMap((r) =>
    r.cities.map((city) => ({
      ...city,
      country: c,
      region: r,
      // Pre-computed search blob (lowercase, accent-stripped basic) for fast filtering
      _search: `${city.ar} ${city.en} ${r.ar} ${r.en} ${c.ar} ${c.en}`.toLowerCase(),
    }))
  )
);

/** Lookup helper used by other pages that store a city id. */
export function findCity(cityId) {
  if (!cityId) return null;
  return ALL_CITIES.find((c) => c.id === cityId) || null;
}

/**
 * SAUDI_CITIES — full Saudi Arabia city list in the compact tuple
 * form `[id, en, ar]` expected by legacy consumers (SellPage,
 * EditListingPage). Kept in `locations.js` so there is exactly ONE
 * source of truth for all city selectors in the platform.
 *
 * Derived at module load from COUNTRIES so any future edit to a
 * region's cities automatically propagates everywhere.
 */
export const SAUDI_CITIES = (
  COUNTRIES.find((c) => c.code === "SA")?.regions || []
).flatMap((r) => r.cities.map((city) => [city.id, city.en, city.ar]));

/**
 * iter240 — Approximate centre coordinates for the major Saudi cities,
 * used ONLY for the "use my location → nearest city" flow on the user's
 * account page. Fully offline / no external geocoding service. Any city
 * not listed here still selectable via the manual dropdown.
 */
export const CITY_COORDS = {
  Riyadh: [24.7136, 46.6753],
  Jeddah: [21.4858, 39.1925],
  Makkah: [21.3891, 39.8579],
  Madinah: [24.4686, 39.6142],
  Dammam: [26.4207, 50.0888],
  Khobar: [26.2172, 50.1971],
  Dhahran: [26.2361, 50.0393],
  Tabuk: [28.3838, 36.5550],
  Abha: [18.2164, 42.5053],
  "Khamis-Mushait": [18.3060, 42.7291],
  Hail: [27.5219, 41.6906],
  Najran: [17.4924, 44.1277],
  Jazan: [16.8892, 42.5511],
  Buraidah: [26.3260, 43.9750],
  Unaizah: [26.0844, 43.9936],
  Hofuf: [25.3789, 49.5910],
  Yanbu: [24.0894, 38.0618],
  Taif: [21.2703, 40.4158],
  Baha: [20.0129, 41.4677],
  Arar: [30.9753, 41.0381],
  Sakakah: [29.9697, 40.2064],
  Kharj: [24.1483, 47.3057],
  Qatif: [26.5209, 49.9962],
  Rabigh: [22.7986, 39.0347],
};

/** Great-circle distance in km between two [lat,lng] points. */
export function haversineKm([lat1, lng1], [lat2, lng2]) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Return the nearest known Saudi city id, or null if `CITY_COORDS` is empty. */
export function nearestSaudiCity(lat, lng) {
  let best = null;
  let bestD = Infinity;
  for (const [id, coords] of Object.entries(CITY_COORDS)) {
    const d = haversineKm([lat, lng], coords);
    if (d < bestD) { bestD = d; best = id; }
  }
  return best;
}
